import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useLang } from "@/i18n/LanguageContext";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Investor = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", interest: "" });
  const [state, setState] = useState({ loading: false, ok: false, err: "" });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.interest.trim()) {
      setState({ loading: false, ok: false, err: t.errors.generic });
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      setState({ loading: false, ok: false, err: t.errors.email });
      return;
    }
    setState({ loading: true, ok: false, err: "" });
    try {
      await axios.post(`${API}/investors`, { ...form, locale: lang });
      setState({ loading: false, ok: true, err: "" });
      setForm({ name: "", email: "", interest: "" });
    } catch (err) {
      setState({ loading: false, ok: false, err: t.errors.generic });
    }
  };

  return (
    <section
      id="investors"
      data-testid="investor-section"
      className="relative py-28 md:py-40 bg-ivory text-forest"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="lg:col-span-5"
          >
            <motion.p variants={fadeUp} className="overline text-bronze mb-6" data-testid="investor-eyebrow">
              {t.investor.eyebrow}
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight"
              data-testid="investor-title"
            >
              {t.investor.title.split("\n").map((line, i) => (
                <span key={i} className="block">
                  {line}
                </span>
              ))}
            </motion.h2>
            <motion.div
              variants={fadeUp}
              className="mt-8 text-base md:text-lg text-forest/75 leading-relaxed max-w-md space-y-5"
              data-testid="investor-body"
            >
              <p>{t.investor.body_1}</p>
              <p>{t.investor.body_2}</p>
              <p>{t.investor.body_3}</p>
            </motion.div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
            className="lg:col-span-6 lg:col-start-7 flex flex-col gap-8"
            data-testid="investor-form"
          >
            <motion.div variants={fadeUp}>
              <label className="overline text-forest/60 block mb-2">{t.investor.name}</label>
              <input
                data-testid="investor-name-input"
                type="text"
                value={form.name}
                onChange={onChange("name")}
                className="input-line"
                required
              />
            </motion.div>
            <motion.div variants={fadeUp}>
              <label className="overline text-forest/60 block mb-2">{t.investor.email}</label>
              <input
                data-testid="investor-email-input"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                className="input-line"
                required
              />
            </motion.div>
            <motion.div variants={fadeUp}>
              <label className="overline text-forest/60 block mb-2">{t.investor.interest}</label>
              <textarea
                data-testid="investor-interest-input"
                rows={4}
                value={form.interest}
                onChange={onChange("interest")}
                className="input-line resize-none"
                required
              />
            </motion.div>
            <motion.div variants={fadeUp} className="flex items-center gap-6 mt-2">
              <button
                type="submit"
                disabled={state.loading}
                className="btn-bronze overline px-8 py-4 disabled:opacity-50"
                data-testid="investor-submit"
              >
                {state.loading ? "..." : t.investor.cta}
              </button>
              {state.ok && (
                <span className="overline text-bronze flex items-center gap-2" data-testid="investor-success">
                  <Check size={14} /> {t.investor.success}
                </span>
              )}
              {state.err && (
                <span className="overline text-bronze" data-testid="investor-error">
                  {state.err}
                </span>
              )}
            </motion.div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
